import { useState, useEffect, useCallback } from "react";
import ApiRequester from "./ApiRequester.jsx";

// ============================================================================
// Configuration
// ============================================================================
const TRIGGER_CONFIG = {
  volume_spike: {
    label: "Volume Spike",
    icon: "📈",
    color: "#e74c3c",
    description: "Residents with a significant increase in call volume this week vs. their baseline"
  },
  sustained_increase: {
    label: "Sustained Increase",
    icon: "📊",
    color: "#e67e22",
    description: "Residents with call volume rising for 3 or more consecutive weeks"
  },
  high_frequency_new: {
    label: "New High-Frequency Caller",
    icon: "🔔",
    color: "#8e44ad",
    description: "Previously low-volume residents now among the facility's highest callers"
  },
  nighttime_increase: {
    label: "Nighttime Increase",
    icon: "🌙",
    color: "#2c3e50",
    description: "Residents with a notable increase in calls between 10pm and 6am"
  },
  reason_shift: {
    label: "Reason Shift",
    icon: "🔄",
    color: "#2980b9",
    description: "Residents showing a significant change in their call reason distribution"
  },
  new_reason: {
    label: "New Reason Appeared",
    icon: "⚠️",
    color: "#16a085",
    description: "Residents calling for reasons not seen in their recent history"
  }
};

const SEVERITY_CONFIG = {
  high: { label: "High", bg: "#fde8e8", text: "#e74c3c", border: "#e74c3c" },
  medium: { label: "Medium", bg: "#fef3e2", text: "#e67e22", border: "#e67e22" },
  low: { label: "Low", bg: "#e8f8f5", text: "#16a085", border: "#16a085" }
};

const TRIGGER_ORDER = [
  "volume_spike", "sustained_increase", "high_frequency_new",
  "nighttime_increase", "reason_shift", "new_reason"
];

// ============================================================================
// Sparkline component for weekly trend visualization
// ============================================================================
function WeeklySparkline({ data, color = "#3498db" }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const h = 32, w = 80;
  const points = data.map((v, i) => ({
    x: (i / (data.length - 1)) * w,
    y: h - (v / max) * (h - 4) - 2
  }));
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");

  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={i === points.length - 1 ? 3 : 2}
          fill={i === points.length - 1 ? color : "#fff"} stroke={color} strokeWidth="1.5" />
      ))}
    </svg>
  );
}

// ============================================================================
// Stat pill for key metrics in each card
// ============================================================================
function StatPill({ label, value, sub }) {
  return (
    <div style={{ textAlign: "center", padding: "6px 12px", background: "var(--color-bg-tertiary)", borderRadius: 6, minWidth: 70 }}>
      <div style={{ fontSize: 11, color: "#8a94a6", marginBottom: 2, whiteSpace: "nowrap" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "#2d3748" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#8a94a6", marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

// ============================================================================
// Individual action item card
// ============================================================================
function ActionCard({ item, onAcknowledge, onDismiss, isProcessing }) {
  const [expanded, setExpanded] = useState(false);
  const severity = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.medium;
  const sd = typeof item.supporting_data === "string" ? JSON.parse(item.supporting_data) : item.supporting_data || {};

  const getWeeklyData = () => {
    if (sd.week1 !== undefined) return [sd.week5, sd.week4, sd.week3, sd.week2, sd.week1].filter(v => v !== undefined);
    if (sd.week1_current !== undefined) return [sd.week5, sd.week4, sd.week3, sd.week2, sd.week1_current].filter(v => v !== undefined);
    if (sd.night_week1 !== undefined) return [sd.night_week5, sd.night_week4, sd.night_week3, sd.night_week2, sd.night_week1].filter(v => v !== undefined);
    return [];
  };

  const getKeyMetrics = () => {
    switch (item.trigger_type) {
      case "volume_spike":
        return [
          { label: "This Week", value: sd.current_week_count },
          { label: "Avg/Week", value: sd.baseline_week_avg },
          { label: "Increase", value: `${sd.percent_increase}%` }
        ];
      case "sustained_increase":
        return [
          { label: "This Week", value: sd.week1_current },
          { label: "Weeks Rising", value: sd.consecutive_weeks_increasing },
        ];
      case "high_frequency_new":
        return [
          { label: "This Week", value: sd.current_week_count },
          { label: "Prior Avg", value: sd.baseline_week_avg },
          { label: "Facility P75", value: sd.facility_p75_current }
        ];
      case "nighttime_increase":
        return [
          { label: "Night Calls", value: sd.night_current_count },
          { label: "Prior Avg", value: sd.night_baseline_week_avg },
          { label: "Night %", value: `${Math.round(sd.night_current_pct)}%`, sub: `was ${Math.round(sd.night_baseline_pct)}%` }
        ];
      case "reason_shift":
        return [
          { label: "Reason", value: sd.reason_name },
          { label: "Now", value: `${Math.round(sd.current_pct)}%` },
          { label: "Was", value: `${Math.round(sd.baseline_pct)}%` }
        ];
      case "new_reason":
        return [
          { label: "Reason", value: sd.reason_name },
          { label: "Count", value: sd.current_count },
          { label: "% of Calls", value: `${Math.round(sd.current_pct)}%` }
        ];
      default:
        return [];
    }
  };

  const weeklyData = getWeeklyData();
  const metrics = getKeyMetrics();
  const triggerColor = TRIGGER_CONFIG[item.trigger_type]?.color || "#3498db";

  return (
    <div style={{
      background: "var(--color-bg-secondary)",
      border: "1px solid #e8ecf1",
      borderRadius: 8,
      marginBottom: 10,
      overflow: "hidden",
      transition: "box-shadow 0.15s ease",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
    }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
    onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)"}
    >
      {/* Card header */}
      <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Severity indicator */}
        <div style={{
          width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
          background: severity.border
        }} />

        {/* Resident info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: "#2d3748" }}>
              {item.resident_first_name} {item.resident_last_name}
            </span>
            <span style={{ fontSize: 12, color: "#8a94a6" }}>
              {item.resident_room}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
              background: severity.bg, color: severity.text, textTransform: "uppercase"
            }}>
              {severity.label}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {item.description}
          </div>
        </div>

        {/* Sparkline */}
        {weeklyData.length > 0 && (
          <div style={{ flexShrink: 0 }}>
            <WeeklySparkline data={weeklyData} color={triggerColor} />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onAcknowledge(item.action_id)}
            disabled={isProcessing}
            title={isProcessing ? "Action not available" : ""}
            style={{
              padding: "6px 14px", fontSize: 12, fontWeight: 500, borderRadius: 6, border: "none",
              background: "#ebf5fb", color: "#2980b9", cursor: isProcessing ? "not-allowed" : "pointer",
              opacity: isProcessing ? 0.6 : 1, transition: "background 0.15s ease"
            }}
            onMouseEnter={e => { if (!isProcessing) e.currentTarget.style.background = "#d4e6f1"; }}
            onMouseLeave={e => e.currentTarget.style.background = "#ebf5fb"}
          >
            Acknowledge
          </button>
          <button
            onClick={() => onDismiss(item.action_id)}
            disabled={isProcessing}
            title={isProcessing ? "Action not available" : ""}
            style={{
              padding: "6px 14px", fontSize: 12, fontWeight: 500, borderRadius: 6, border: "none",
              background: "#f5f5f5", color: "#8a94a6", cursor: isProcessing ? "not-allowed" : "pointer",
              opacity: isProcessing ? 0.6 : 1, transition: "background 0.15s ease"
            }}
            onMouseEnter={e => { if (!isProcessing) e.currentTarget.style.background = "#e8e8e8"; }}
            onMouseLeave={e => e.currentTarget.style.background = "#f5f5f5"}
          >
            Dismiss
          </button>
        </div>

        {/* Expand arrow */}
        <span style={{
          fontSize: 12, color: "#8a94a6", transition: "transform 0.2s ease", flexShrink: 0,
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)"
        }}>
          ▼
        </span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: "0 16px 14px 28px", borderTop: "1px solid #f0f2f5" }}>
          <div style={{ paddingTop: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            {metrics.map((m, i) => (
              <StatPill key={i} label={m.label} value={m.value} sub={m.sub} />
            ))}
            {weeklyData.length > 0 && (
              <div style={{ marginLeft: "auto", textAlign: "right" }}>
                <div style={{ fontSize: 10, color: "#8a94a6", marginBottom: 4 }}>5-Week Trend</div>
                <WeeklySparkline data={weeklyData} color={triggerColor} />
                <div style={{ fontSize: 9, color: "#adb5bd", marginTop: 2, display: "flex", justifyContent: "space-between" }}>
                  <span>5 wks ago</span><span>This wk</span>
                </div>
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 10, lineHeight: 1.5 }}>
            {item.description}
          </div>
          <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 8 }}>
            Analysis: {item.window_start} — {item.window_end}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Trigger group section
// ============================================================================
function TriggerSection({ triggerType, items, summaryItems, onAcknowledge, onDismiss, processingIds }) {
  const [collapsed, setCollapsed] = useState(false);
  const config = TRIGGER_CONFIG[triggerType] || { label: triggerType, icon: "📋", color: "#3498db" };

  const totalCount = summaryItems
    .filter(s => s.trigger_type === triggerType)
    .reduce((sum, s) => sum + s.item_count, 0);

  const highCount = summaryItems
    .filter(s => s.trigger_type === triggerType && s.severity === "high")
    .reduce((sum, s) => sum + s.item_count, 0);

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Section header */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 0",
          cursor: "pointer", userSelect: "none"
        }}
      >
        <span style={{ fontSize: 16 }}>{config.icon}</span>
        <span style={{ fontSize: 15, fontWeight: 600, color: "#2d3748" }}>{config.label}</span>
        <span style={{
          fontSize: 12, fontWeight: 600, padding: "2px 10px", borderRadius: 10,
          background: highCount > 0 ? "#fde8e8" : "#f0f2f5",
          color: highCount > 0 ? "#e74c3c" : "#6b7280"
        }}>
          {totalCount}
        </span>
        <span style={{ fontSize: 12, color: "#adb5bd", flex: 1 }}>{config.description}</span>
        <span style={{
          fontSize: 12, color: "#8a94a6", transition: "transform 0.2s ease",
          transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)"
        }}>
          ▼
        </span>
      </div>

      {/* Cards */}
      {!collapsed && (
        <div>
          {items.map(item => (
            <ActionCard
              key={item.action_id}
              item={item}
              onAcknowledge={onAcknowledge}
              onDismiss={onDismiss}
              isProcessing={true}
              // isProcessing={processingIds.has(item.action_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================
export default function ResidentTrendActions(props) {
  const [data, setData] = useState({ action_items: [], summary_items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingIds, setProcessingIds] = useState(new Set());

  // Replace with your actual campusId source
  const campusId = props?.campus_id;

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    ApiRequester({reqEndpoint: "get-mysql-report", rep_type: "residentactiontrends", campus_id: campusId,
                        status: "new"}).then(data => {
                          setData(data);
                          setLoading(false);
                        }).catch(err => {
                          setError("Failed to load action items");
                          setLoading(false)});

    // setTimeout(() => {
    //   setData(MOCK_DATA);
    //   setLoading(false);
    // }, 600);
  }, [campusId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAcknowledge = useCallback((actionId) => {
    setProcessingIds(prev => new Set(prev).add(actionId));

    // ApiRequester({
    //   reqEndpoint: "update-mysql-report",
    //   rep_type: "residentactionupdate",
    //   action_id: actionId,
    //   status: "acknowledged",
    //   user_id: currentUserId
    // }).then(() => {
    //   setData(prev => ({
    //     ...prev,
    //     action_items: prev.action_items.filter(i => i.action_id !== actionId)
    //   }));
    //   setProcessingIds(prev => { const n = new Set(prev); n.delete(actionId); return n; });
    // });

    setTimeout(() => {
      setData(prev => ({
        ...prev,
        action_items: prev.action_items.filter(i => i.action_id !== actionId)
      }));
      setProcessingIds(prev => { const n = new Set(prev); n.delete(actionId); return n; });
    }, 400);
  }, []);

  const handleDismiss = useCallback((actionId) => {
    setProcessingIds(prev => new Set(prev).add(actionId));

    // ----- Replace with your real API call: -----
    setTimeout(() => {
      setData(prev => ({
        ...prev,
        action_items: prev.action_items.filter(i => i.action_id !== actionId)
      }));
      setProcessingIds(prev => { const n = new Set(prev); n.delete(actionId); return n; });
    }, 400);
  }, []);

  // Group items by trigger type in defined order
  const groupedItems = TRIGGER_ORDER
    .map(type => ({
      type,
      items: data.action_items.filter(i => i.trigger_type === type)
    }))
    .filter(g => g.items.length > 0);

  const totalActive = data.action_items.length;
  const totalHigh = data.action_items.filter(i => i.severity === "high").length;

  return (
    <div style={{
      background: "var(--color-bg-secondary)",
      borderRadius: 8,
      border: "1px solid #e8ecf1",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header bar — matches dashboard card style */}
      <div style={{
        background: "#1a8a7d",
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ color: "#fff", fontSize: 15, fontWeight: 600 }}>Resident Trends & Actions</span>
          {totalActive > 0 && (
            <span style={{
              fontSize: 12, fontWeight: 600, padding: "2px 10px", borderRadius: 10,
              background: totalHigh > 0 ? "rgba(231,76,60,0.25)" : "rgba(255,255,255,0.2)",
              color: "#fff"
            }}>
              {totalActive} item{totalActive !== 1 ? "s" : ""}
              {totalHigh > 0 && ` · ${totalHigh} high`}
            </span>
          )}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          style={{
            padding: "5px 14px", fontSize: 12, fontWeight: 500, borderRadius: 6,
            border: "1px solid rgba(255,255,255,0.3)", background: "transparent",
            color: "#fff", cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: "8px 20px 16px" }}>
        {loading && data.action_items.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#8a94a6", fontSize: 14 }}>
            Loading action items...
          </div>
        ) : error ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#e74c3c", fontSize: 14 }}>
            {error}
            <div style={{ marginTop: 8 }}>
              <button onClick={fetchData} style={{
                padding: "6px 16px", fontSize: 12, borderRadius: 6, border: "1px solid #e74c3c",
                background: "var(--color-bg-secondary)", color: "#e74c3c", cursor: "pointer"
              }}>Retry</button>
            </div>
          </div>
        ) : groupedItems.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
            <div style={{ color: "#2d3748", fontSize: 14, fontWeight: 500 }}>No action items</div>
            <div style={{ color: "#8a94a6", fontSize: 13, marginTop: 4 }}>All resident trends are within normal ranges</div>
          </div>
        ) : (
          groupedItems.map(group => (
            <TriggerSection
              key={group.type}
              triggerType={group.type}
              items={group.items}
              summaryItems={data.summary_items}
              onAcknowledge={handleAcknowledge}
              onDismiss={handleDismiss}
              processingIds={processingIds}
            />
          ))
        )}
      </div>
    </div>
  );
}